import React, { useState, useId } from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { CHALLENGES } from '../../data/vocabulary';
import Reveal from '../Reveal';

const LETTERS = ['A', 'B', 'C', 'D'];
const BURST = [
  { angle: 0, color: 'bg-tangerine' },
  { angle: 45, color: 'bg-lavender' },
  { angle: 90, color: 'bg-butter' },
  { angle: 135, color: 'bg-leaf' },
  { angle: 180, color: 'bg-tangerine' },
  { angle: 225, color: 'bg-bluebell' },
  { angle: 270, color: 'bg-butter' },
  { angle: 315, color: 'bg-leaf' },
];

// Everyone starts on the same question each day, then can keep going
const firstQuestionToday = new Date().getDate() % CHALLENGES.length;

const optionClass = (index, { choice, submitted, answer }) => {
  const base =
    'flex w-full cursor-pointer items-center gap-4 rounded-2xl border-2 bg-surface px-4 py-3.5 text-left text-[1.05rem] text-ink transition-[border-color,background-color,opacity,transform] duration-200 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-peach';

  if (!submitted) {
    return `${base} ${choice === index ? 'border-ink' : 'border-transparent hover:border-ink/25'} active:scale-[0.99]`;
  }
  if (index === answer) return `${base} cursor-default border-leaf bg-mint`;
  if (index === choice) return `${base} cursor-default border-rose`;
  return `${base} cursor-default border-transparent opacity-55`;
};

const WordChallenge = () => {
  const [index, setIndex] = useState(firstQuestionToday);
  const [choice, setChoice] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState({ right: 0, answered: 0 });
  const groupName = useId();

  const question = CHALLENGES[index];
  const isCorrect = submitted && choice === question.answer;
  const state = { choice, submitted, answer: question.answer };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (choice === null || submitted) return;
    setSubmitted(true);
    setScore((s) => ({ right: s.right + (choice === question.answer ? 1 : 0), answered: s.answered + 1 }));
  };

  const nextQuestion = () => {
    setIndex((i) => (i + 1) % CHALLENGES.length);
    setChoice(null);
    setSubmitted(false);
  };

  return (
    <section aria-labelledby="challenge-heading" className="wrap pb-16 md:pb-24">
      <Reveal className="grid gap-10 rounded-[2rem] bg-peach p-7 sm:p-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-14 lg:p-14">
        <div>
          <h2 id="challenge-heading" className="font-display text-4xl leading-tight text-ink md:text-5xl">
            Today’s word challenge
          </h2>
          <p className="mt-4 max-w-md text-lg leading-relaxed text-ink">
            Pick the meaning you think is right. There’s a new first question every day, and you can keep going as
            long as you like.
          </p>
          {score.answered > 0 && (
            <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-surface/75 px-4 py-2 text-[0.95rem] font-medium text-ink">
              This visit: {score.right} of {score.answered} correct
            </p>
          )}
          <p className="mt-6 max-w-md text-sm text-ink-soft">
            Practice questions written for this site. Each answer links to the word’s full dictionary entry.
          </p>
        </div>

        <form onSubmit={handleSubmit} key={index} className="feedback-in">
          <fieldset disabled={submitted}>
            <legend className="font-display text-2xl leading-snug text-ink sm:text-3xl">
              What does <span className="rounded-lg bg-surface/80 px-2">{question.word}</span> mean?
            </legend>

            <div className="mt-6 space-y-3">
              {question.options.map((option, i) => (
                <label key={option} className={optionClass(i, state)}>
                  <input
                    type="radio"
                    name={groupName}
                    value={i}
                    checked={choice === i}
                    onChange={() => setChoice(i)}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold ${
                      choice === i && !submitted ? 'bg-ink text-paper' : 'bg-peach text-ink'
                    }`}
                  >
                    {LETTERS[i]}
                  </span>
                  <span className="flex-1">{option}</span>
                  {submitted && i === question.answer && <Check size={20} className="shrink-0 text-leaf" aria-label="Correct answer" />}
                  {submitted && i === choice && i !== question.answer && <X size={20} className="shrink-0 text-rose" aria-label="Your answer" />}
                </label>
              ))}
            </div>
          </fieldset>

          {!submitted ? (
            <button
              type="submit"
              disabled={choice === null}
              className="mt-6 h-12 rounded-full bg-ink px-7 font-semibold text-paper transition-[opacity,transform] hover:bg-ink/85 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-35"
            >
              Submit answer
            </button>
          ) : (
            <div className="feedback-in mt-6 rounded-2xl bg-surface/85 p-5 sm:p-6" role="status">
              <div className="flex items-center gap-3">
                <span className="relative grid h-10 w-10 shrink-0 place-items-center">
                  {isCorrect &&
                    BURST.map(({ angle, color }) => (
                      <span key={angle} aria-hidden="true" className={`burst ${color}`} style={{ '--angle': `${angle}deg` }} />
                    ))}
                  <span className={`pop grid h-10 w-10 place-items-center rounded-full ${isCorrect ? 'bg-leaf text-paper' : 'bg-rose text-paper'}`}>
                    {isCorrect ? <Check size={20} aria-hidden="true" /> : <X size={20} aria-hidden="true" />}
                  </span>
                </span>
                <p className="font-display text-2xl text-ink">{isCorrect ? 'Correct!' : 'Not quite'}</p>
              </div>
              {!isCorrect && (
                <p className="mt-3 text-ink">
                  The answer is <strong className="font-semibold">{question.options[question.answer].toLowerCase()}</strong>.
                </p>
              )}
              <p className="mt-2 leading-relaxed text-ink-soft">{question.explanation}</p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={nextQuestion}
                  className="h-11 rounded-full bg-ink px-6 font-semibold text-paper transition-[background-color,transform] hover:bg-ink/85 active:scale-[0.97]"
                >
                  Next challenge
                </button>
                <Link to={`/define/${question.word}`} className="text-[0.95rem] text-link">
                  See the full definition of {question.word}
                </Link>
              </div>
            </div>
          )}
        </form>
      </Reveal>
    </section>
  );
};

export default WordChallenge;
